/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import Header from 'components/header/Header';
import AccessHeader from 'modules/access/components/access-header/AccessHeader';
import Spinner from 'components/spinner/Spinner';
import PageNotFound from 'components/page/page-not-found/PageNotFound';
import VersionChecker from 'components/version-checker/VersionChecker';
import ConfirmModal from 'components/modal/confirm-modal/ConfirmModal';
import { MobileAppBanner, UnsupportedBrowserBanner, withMobileBanner } from 'components/banners';
import BasicHeaderWithLogo from 'components/BasicHeaderWithLogo';
import FacebookButtonInitialize from 'components/external-integration/facebook/button/common/initialize/FacebookButtonInitialize';

import { PAGE_TYPE, redirectToAppStorePage } from 'utils/routes';

import './main-layout.scss';

import type {
  InfluencerProfile,
  Organization
} from 'types';
import AcceptTeamInviteModalManager from 'components/accept-team-invite-modal/AcceptTeamInviteModalManager';

const FloatingMobileAppBanner = withMobileBanner(MobileAppBanner, 'mobileAppBanner');

type OwnProps = {
  pageType?: string,
  children?: React.ReactNode,
  params?: {
    [key: string]: string
  },
  shouldRedirectToAppStorePage: boolean
};

type StateProps = {
  influencerProfile: InfluencerProfile,
  currentOrganization: Organization,
  confirmModalProps: {
    onConfirm: () => void,
    onCancel?: () => void
  },
  isConfirmModalVisible: boolean,
  isLoadingVisible: boolean,
  userId: number,
  isNewAccount: boolean
};

type Props = OwnProps & StateProps;



class MainLayout extends React.Component<Props> {
  componentDidMount() {
    const { shouldRedirectToAppStorePage } = this.props;

    if (shouldRedirectToAppStorePage) {
      redirectToAppStorePage();
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    const {
      pageType,
      children,
      params,
      influencerProfile,
      confirmModalProps,
      isConfirmModalVisible,
      isLoadingVisible,
      userId
    } = this.props;

    return pageType !== nextProps.pageType
      || !isEqual(children, nextProps.children)
      || !isEqual(params, nextProps.params)
      || !isEqual(influencerProfile, nextProps.influencerProfile)
      || !isEqual(confirmModalProps, nextProps.confirmModalProps)
      || isConfirmModalVisible !== nextProps.isConfirmModalVisible
      || isLoadingVisible !== nextProps.isLoadingVisible
      || nextProps.userId !== userId;
  }

  checkParamFormat(params, paramName) {
    if (!(paramName in params)) {
      return true;
    }

    // if we have 'id' in params it must be a number
    return !isNaN(Number(params[paramName])); // eslint-disable-line no-restricted-globals
  }

  isParamsFormatRight(params) {
    // we use 'id' and 'subcampaignId' in routes
    return this.checkParamFormat(params, 'id') && this.checkParamFormat(params, 'subcampaignId');
  }

  getSpinner({ isLoadingVisible }) {
    return isLoadingVisible && <Spinner />;
  }

  getConfirmModal({ isConfirmModalVisible, confirmModalProps }: Props) {
    return (
      <ConfirmModal
        open={isConfirmModalVisible}
        {...confirmModalProps}
      />
    );
  }

  renderAccessLayout = () => {
    const { children, params } = this.props;

    const classes = classnames({
      'layout-page': true,
      'layout-page--access': true,
      'layout-page--access-background': true
    });

    return (
      <section className={classes}>
        {this.getSpinner(this.props)}
        <AccessHeader />
        <VersionChecker />
        <div className="layout-page__content">
          {
            this.isParamsFormatRight(params) ?
              children :
              <PageNotFound />
          }
        </div>
        {this.getConfirmModal(this.props)}
      </section>
    );
  };

  renderDefaultLayout = () => {
    const {
      children,
      params,
      pageType,
      influencerProfile,
      currentOrganization,
      isNewAccount,
      userId
    } = this.props;

    const classes = classnames({
      'layout-page': true,
      'layout-page--default': !pageType,
      [`layout-page--${pageType}`]: pageType
    });

    const creatorProfileId = influencerProfile?.id;

    return (
      <section className={classes}>
        {this.getSpinner(this.props)}
        <FloatingMobileAppBanner />
        <UnsupportedBrowserBanner />
        <Header />
        <VersionChecker />
        {
          !isNil(creatorProfileId) && currentOrganization.type === 'influencer' && (
            <FacebookButtonInitialize
              key={`main-layout__facebook-button-initialize--${creatorProfileId}`}
              creatorProfileId={creatorProfileId}
            />
          )
        }
        <AcceptTeamInviteModalManager
          userId={userId}
          isNewAccount={isNewAccount}
        />
        <div className="layout-page__content">
          {
            this.isParamsFormatRight(params) ?
              children :
              <PageNotFound />
          }
        </div>
        {this.getConfirmModal(this.props)}
      </section>
    );
  };

  renderEmptyLayout = () => {
    const {
      children,
      params,
      pageType
    } = this.props;

    const classes = classnames({
      'layout-page': true,
      [`layout-page--${pageType}`]: pageType
    });

    return (
      <section className={classes}>
        {this.getSpinner(this.props)}
        <div className="layout-page__content">
          {
            this.isParamsFormatRight(params) ?
              children :
              <PageNotFound />
          }
        </div>
        {this.getConfirmModal(this.props)}
      </section>
    );
  };

  renderExternalLayout() {
    return (
      <section className="layout-page layout-page--shared-access">
        {this.getSpinner(this.props)}
        {this.getConfirmModal(this.props)}
        <UnsupportedBrowserBanner />
        <BasicHeaderWithLogo logoProps={{ type: 'image' }} />
        <div className="layout-page__content">
          {this.props.children}
        </div>
      </section>
    );
  }

  render() {
    const { pageType } = this.props;

    switch (pageType) {
      case PAGE_TYPE.EMPTY:
      case PAGE_TYPE.CONSUMER_INSIGHTS:
        return this.renderEmptyLayout();
      case PAGE_TYPE.ACCESS:
        return this.renderAccessLayout();
      case PAGE_TYPE.SHARED_ACCESS:
        return this.renderExternalLayout();
      default:
        return this.renderDefaultLayout();
    }
  }
}

const mapStateToProps = ({
  loading,
  modal,
  influencerProfile,
  organizations,
  account
}): StateProps => ({
  isNewAccount: organizations.list.length === 0,
  userId: account?.currentAccount?.id,
  isLoadingVisible: loading.isVisible,
  confirmModalProps: modal.confirmModalProps,
  isConfirmModalVisible: modal.isConfirmModalVisible,
  influencerProfile: influencerProfile.profile,
  currentOrganization: organizations.currentOrganization
});

export default connect(mapStateToProps)(MainLayout);
